// TODO: fix warn Warning ⚠️: The RichTextResolver class is deprecated and will be removed in the next major release.
// Please use the `@storyblok/richtext` package instead. https://github.com/storyblok/richtext/
/* eslint-disable max-len */
import React, { FC } from 'react';
import { BlokToComponentProps } from '../../helpers/blok-to-component/types';
import { RocheUIRichText } from '@repo/roche-ui';

import StoryblokClient, { ISbRichtext } from 'storyblok-js-client';
import { Link } from '../../services/navigation/types';
import { maskAssetUrl, multiLink } from '../../utils';

export interface TextLinkComponentProps {
  text?: string;
  target?: string;
  left_icon?: string;
  right_icon?: string;
  link: Link;
}

interface HeadlineComponentProps {
  headline: string;
  level: number;
}

interface OverlineComponentProps {
  overline_text: string;
}
interface IconComponentProps {
  icon?: string;
  height?: string;
}

interface CookieBannerLinkComponentProps {
  text?: string;
}

interface RichtextComponentConfiguration {
  footnote: boolean;
}

const unwantedMarks = ['textStyle', 'superscript', 'subscript'];

const sanitizeText = (text = '') => text.replace(/"/g, '&quot;');

const componentPropRenderer = {
  'roche-text-link': (blok: TextLinkComponentProps): string => {
    if (typeof blok.link !== 'object') {
      return null;
    }

    const { href } = multiLink('href', blok.link);

    /* Dev-note: Since this component is also used to link to assets, masking the url is required,
     * otherwise crawlers pick up the original link to Storyblok assets manager
     */
    return `href="${maskAssetUrl(href)}" text="${sanitizeText(blok.text)}" target="${blok.target}" left-icon="${blok.left_icon}" right-icon="${blok.right_icon}"`;
  },

  'roche-headline': (blok: HeadlineComponentProps): string =>
    `level="${blok.level}" headline="${sanitizeText(blok.headline)}"`,

  'roche-overline': (blok: OverlineComponentProps): string => `overline-text="${sanitizeText(blok.overline_text)}"`,

  'roche-icon': (blok: IconComponentProps): string => `height="${blok.height}" icon="${blok.icon}"`,

  'roche-cookie-banner-link': (blok: CookieBannerLinkComponentProps) =>
    `text="${sanitizeText(blok.text)}" onclick="window.OneTrust?.ToggleInfoDisplay()"`,
};

const storyblokClient = new StoryblokClient({
  accessToken: process.env.GATSBY_STORYBLOK_SPACE_API_KEY,
});

storyblokClient.setComponentResolver(
  (component, blok): string => `<${component} ${componentPropRenderer[component](blok)}></${component}>`,
);

/**
 * Storyblok's richtext editor always forces blok level components to close the previous "tag".
 *
 * This means we need to combine the <p> tags surrounding the roche-text-link into the same one,
 * with roche-text-link as their child
 *
 * In order to maximize reusability of Storyblok's richTextResolver,
 * we use pattern matching and work directly on the "stringified markup" output by their tools.
 *
 * NOTE: The editor can still make links behave like their own Blok,
 * by inserting 2 carriage returns, instead of 1, after a paragraph.
 */
const forceLinksInsideParagraphs = (markupString: string, { footnote }: RichtextComponentConfiguration): string =>
  markupString
    .replace(/<roche-text-link/gm, `<roche-text-link ${footnote ? 'size="footnote"' : ''}`)
    .replace(/<\/p><roche-cookie-banner-link/gm, ' <roche-text-link')
    .replace(/<\/roche-cookie-banner-link><p>/gm, '</roche-text-link> ')
    .replace(/<\/p><roche-text-link/gm, ' <roche-text-link')
    .replace(/<\/roche-text-link><p>/gm, '</roche-text-link> ')
    .replace(/<p><\/p>/, '');

/**
 * External Links are written by the Storyblok helper as `href="/http://…"`
 * However, they should not have the initial slash. This function removes it.
 */
const fixExternalLinks = (markupString: string): string =>
  markupString.replace(/href="\/https:\/\//gm, 'href="https://').replace(/href="\/http:\/\//gm, 'href="http://');

const markupFromRichtextField = (
  storyblokHtmlSchema: ISbRichtext,
  configuration: RichtextComponentConfiguration,
): string => {
  // Check if content exists and is properly structured as an array of nodes
  if (!Array.isArray(storyblokHtmlSchema.content)) {
    return '';
  }
  const sanitizedSchema = removeUnwantedMarks(storyblokHtmlSchema);

  return fixExternalLinks(
    forceLinksInsideParagraphs(storyblokClient.richTextResolver.render(sanitizedSchema), configuration),
  );
};

const getOppositeDirValue = (dirValue) => (dirValue === 'ltr' ? 'rtl' : 'ltr');

const getGlobalDirValue = () => {
  const dirValue = typeof window !== 'undefined' ? document.querySelector('html').getAttribute('dir') : 'ltr'; // Provide a default value if not in the browser environment

  return dirValue;
};

type RocheRichtextProps = BlokToComponentProps<{
  text: ISbRichtext;
  footnote: boolean;
  reverse_text_direction: boolean;
  type: string;
}>;

const removeUnwantedMarks = (node: ISbRichtext) => {
  if (node.marks) {
    node.marks = node.marks.filter((mark) => !unwantedMarks.includes(mark.type));
  }
  if (node.content) {
    node.content.forEach((child) => removeUnwantedMarks(child));
  }
  return node;
};

export const RocheRichtext: FC<RocheRichtextProps> = ({ blok, slot }) => {
  const markup = markupFromRichtextField(blok.text, {
    footnote: blok?.footnote,
  });
  const textDirection = blok?.reverse_text_direction ? getOppositeDirValue(getGlobalDirValue()) : getGlobalDirValue();

  return (
    <RocheUIRichText
      slot={slot}
      dir={textDirection}
      markup={markup}
      isFootnote={blok.footnote}
      isFooterType={blok.type === 'footer'}
    />
  );
};
